// Internal GQL query strings. Not exported from the SDK — the client formats
// raw responses before returning.

// ─────────────────────────────────────────────────────────────────────────────
// Pairs (sorted by id)
// ─────────────────────────────────────────────────────────────────────────────

const PAIR_FIELDS = `
    id
    from
    to
    maxLeverage
    overnightMaxLeverage
    takerFeeP
    makerFeeP
    makerMaxLeverage
    maxOI
    longOI
    shortOI
    fee { oracleFee minLevPos }
    group { id name maxLeverage minLeverage longCollateral shortCollateral maxCollateralP }
    lastUpdateTimestamp
    buyVolume
    sellVolume
    decayRate
    netVolThreshold
    priceImpactK
    accRolloverLong
    accRolloverShort
    lastRolloverBlock
    lastRolloverLongPure
    brokerPremium
    isNegativeRolloverAllowed
    lastTradePrice
    spreadP`;

export const GetPairsAndGroupsQuery = `
query GetPairsAndGroups {
  pairs(orderBy: id, orderDirection: asc, subgraphError: allow) {
    ${PAIR_FIELDS}
  }
  groups { id name maxLeverage minLeverage longCollateral shortCollateral maxCollateralP }
}`;

// ─────────────────────────────────────────────────────────────────────────────
// Open trades — full pair sub-fields needed for live PnL calculations
// ─────────────────────────────────────────────────────────────────────────────

const PAIR_FULL = `
    pair {
      id from to
      maxLeverage overnightMaxLeverage
      takerFeeP makerFeeP makerMaxLeverage
      maxOI longOI shortOI
      fee { oracleFee minLevPos }
      group { id name maxLeverage minLeverage longCollateral shortCollateral maxCollateralP }
      lastUpdateTimestamp
      buyVolume sellVolume decayRate netVolThreshold priceImpactK
      accRolloverLong accRolloverShort lastRolloverBlock lastRolloverLongPure
      brokerPremium isNegativeRolloverAllowed lastTradePrice
    }`;

const TRADE_CORE = `
    id tradeID trader
    isOpen isBuy isDayTrade
    index tradeType
    collateral notional tradeNotional leverage highestLeverage
    openPrice stopLossPrice takeProfitPrice
    rollover timestamp`;

export const GetTraderOpenTradesQuery = `
query GetTraderOpenTrades($trader: String!, $skip: Int!, $first: Int!) {
  trades(
    where: { isOpen: true, trader: $trader }
    skip: $skip first: $first
    orderBy: timestamp orderDirection: desc
  ) {
    ${TRADE_CORE}
    ${PAIR_FULL}
  }
}`;

export const GetAllOpenTradesQuery = `
query GetAllOpenTrades($skip: Int!, $first: Int!) {
  trades(
    where: { isOpen: true }
    skip: $skip first: $first
    orderBy: timestamp orderDirection: desc
  ) {
    ${TRADE_CORE}
    ${PAIR_FULL}
  }
}`;

// ─────────────────────────────────────────────────────────────────────────────
// Executed orders — fills
// ─────────────────────────────────────────────────────────────────────────────

const ORDER_FIELDS = `
    id tradeID limitID trader
    pair { id from to group { id name } }
    orderAction orderType isBuy
    isPending isCancelled cancelReason
    collateral notional tradeNotional leverage
    price priceAfterImpact priceImpactP
    vaultFee devFee oracleFee rolloverFee liquidationFee
    builder builderFee
    profitPercent totalProfitPercent amountSentToTrader
    closePercent
    initiatedTx initiatedBlock initiatedAt
    executedTx executedBlock executedAt`;

/**
 * Build an executed-fills query with optional filters. Pass `false` for any
 * filter to omit it from both the query signature and the `where` clause.
 */
export function buildExecutedFillsQuery(opts: {
  hasTrader: boolean;
  hasPairId: boolean;
  hasTimeRange: boolean;
}): string {
  const args: string[]      = ['$skip: Int!', '$first: Int!'];
  const conditions: string[] = ['isPending: false', 'isCancelled: false'];

  if (opts.hasTrader) {
    args.push('$trader: String!');
    conditions.push('trader: $trader');
  }
  if (opts.hasPairId) {
    args.push('$pairId: String!');
    conditions.push('pair: $pairId');
  }
  if (opts.hasTimeRange) {
    args.push('$startSec: BigInt!', '$endSec: BigInt!');
    conditions.push('executedAt_gte: $startSec', 'executedAt_lte: $endSec');
  }

  return `
query GetExecutedFills(${args.join(', ')}) {
  orders(
    where: { ${conditions.join(', ')} }
    skip: $skip first: $first
    orderBy: executedAt orderDirection: desc
  ) { ${ORDER_FIELDS} }
}`;
}

/**
 * Build a query for orders at any status (pending, executed, cancelled).
 * - `hasOrderIds`: filter by `id_in` (on-chain order ids).
 * - `hasInitiatedTxHashes`: filter by `initiatedTx_in` (submission tx hashes).
 * - `hasTrader`: filter by trader address.
 * Other filters compose with those lookup modes.
 */
export function buildGetOrdersQuery(opts: {
  hasOrderIds: boolean;
  hasInitiatedTxHashes: boolean;
  hasTrader: boolean;
  hasBuilder: boolean;
  hasIsPending: boolean;
  hasIsCancelled: boolean;
  hasPairIds: boolean;
  hasTradeIds: boolean;
  hasExcludeIds: boolean;
  hasExecutedStart: boolean;
  hasExecutedEnd: boolean;
}): string {
  const args: string[] = ['$skip: Int!', '$first: Int!'];
  const conditions: string[] = [];

  if (opts.hasOrderIds) {
    args.push('$ids: [String!]!');
    conditions.push('id_in: $ids');
  }
  if (opts.hasInitiatedTxHashes) {
    args.push('$txHashes: [String!]!');
    conditions.push('initiatedTx_in: $txHashes');
  }
  if (opts.hasTrader) {
    args.push('$trader: String!');
    conditions.push('trader: $trader');
  }
  if (opts.hasBuilder) {
    args.push('$builder: String!');
    conditions.push('builder: $builder');
  }
  if (opts.hasIsPending) {
    args.push('$isPending: Boolean!');
    conditions.push('isPending: $isPending');
  }
  if (opts.hasIsCancelled) {
    args.push('$isCancelled: Boolean!');
    conditions.push('isCancelled: $isCancelled');
  }
  if (opts.hasPairIds) {
    args.push('$pairIds: [String!]!');
    conditions.push('pair_in: $pairIds');
  }
  if (opts.hasTradeIds) {
    args.push('$tradeIds: [String!]!');
    conditions.push('tradeID_in: $tradeIds');
  }
  if (opts.hasExcludeIds) {
    args.push('$excludeIds: [String!]!');
    conditions.push('id_not_in: $excludeIds');
  }
  if (opts.hasExecutedStart) {
    args.push('$executedStartSec: BigInt!');
    conditions.push('executedAt_gte: $executedStartSec');
  }
  if (opts.hasExecutedEnd) {
    args.push('$executedEndSec: BigInt!');
    conditions.push('executedAt_lte: $executedEndSec');
  }

  const where = conditions.length > 0 ? `where: { ${conditions.join(', ')} }` : '';

  return `
query GetOrders(${args.join(', ')}) {
  orders(
    ${where}
    skip: $skip first: $first
    orderBy: initiatedAt orderDirection: desc
  ) { ${ORDER_FIELDS} }
}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Active limit orders (sorted by updatedAt desc)
// ─────────────────────────────────────────────────────────────────────────────

const LIMIT_FIELDS = `
    id uniqueId orderId trader
    pair { id from to group { id name } }
    isBuy limitType isActive executionStarted
    collateral notional tradeNotional leverage
    openPrice takeProfitPrice stopLossPrice
    block initiatedAt updatedAt`;

export const GetTraderActiveLimitsQuery = `
query GetTraderActiveLimits($trader: String!, $skip: Int!, $first: Int!) {
  limits(
    where: { isActive: true, trader: $trader }
    skip: $skip first: $first
    orderBy: updatedAt orderDirection: desc
  ) { ${LIMIT_FIELDS} }
}`;

// ─────────────────────────────────────────────────────────────────────────────
// Account updates snapshot — pending orders, active limits, open trades
// ─────────────────────────────────────────────────────────────────────────────

export const GetAccountUpdatesSnapshotQuery = `
query GetAccountUpdatesSnapshot($traders: [String!]!, $first: Int!, $closedSince: BigInt!) {
  orders(
    where: { trader_in: $traders, isPending: true, isCancelled: false, orderType: "Market" }
    first: $first
    orderBy: initiatedAt orderDirection: desc
  ) { ${ORDER_FIELDS} }
  limits(
    where: { trader_in: $traders, isActive: true }
    first: $first
    orderBy: updatedAt orderDirection: desc
  ) { ${LIMIT_FIELDS} }
  trades(
    where: { trader_in: $traders, isOpen: true }
    first: $first
    orderBy: timestamp orderDirection: desc
  ) {
    ${TRADE_CORE}
    ${PAIR_FULL}
  }
  closedOrders: orders(
    where: { trader_in: $traders, isPending: false, isCancelled: false, orderAction_not: "Open", executedAt_gte: $closedSince }
    first: $first
    orderBy: executedAt orderDirection: desc
  ) { ${ORDER_FIELDS} }
}`;

// ─────────────────────────────────────────────────────────────────────────────
// Vault — protocol balance, required by `WithinExposureLimit`
// ─────────────────────────────────────────────────────────────────────────────

export const GetVaultQuery = `
query GetVault {
  vault(id: "usdc-vault", subgraphError: allow) {
    accPnlPerTokenUsed
    shares
    rewardsPerToken
  }
}`;
